/**
 * /frontend/src/features/analytics/utils/csvExport.js
 *
 * Shared CSV exporter for the merchant portal. Every download is branded with
 * Zopeo + the current store's name so exported files are self-identifying.
 */

import store from '../../../app/providers/store';

const BRAND_NAME = 'Zopeo';

/**
 * Resolves the branding preamble for an export. The store name is read from
 * redux by default so every callsite is branded with zero plumbing; callers
 * may override via `options.storeName` or opt out entirely with `brand: false`.
 */
const resolveBrandLines = (options = {}) => {
  if (options.brand === false) {
    return [];
  }
  let storeName = options.storeName;
  if (storeName === undefined) {
    try {
      storeName = store?.getState?.()?.store?.storeName || '';
    } catch (error) {
      storeName = '';
    }
  }
  // Single combined masthead — "Zopeo | <Store Name>" — rather than one cell
  // per token, so it reads as a title band instead of stray data cells.
  const masthead = storeName ? `${BRAND_NAME} | ${storeName}` : BRAND_NAME;
  const lines = [masthead];
  if (options.title) {
    lines.push(String(options.title));
  }
  (options.meta || []).forEach((line) => {
    if (line) lines.push(String(line));
  });
  lines.push(`Generated: ${new Date().toLocaleString()}`);
  lines.push(''); // blank separator before the table
  return lines;
};

/**
 * Renders a preamble line as a CSV row that sits in the middle column, so a
 * spreadsheet shows the branding centered above the table rather than jammed
 * into column A. Blank lines stay empty.
 */
const centerLineToRow = (text, width) => {
  if (!text) return '';
  if (!Number.isFinite(width) || width <= 1) {
    return escapeCell(text);
  }
  const mid = Math.floor((width - 1) / 2);
  const cells = new Array(width).fill('""');
  cells[mid] = escapeCell(text);
  return cells.join(',');
};

/**
 * Builds the branded CSV string (pure — no DOM side effects).
 * @param {Array<Record<string, unknown>>} rows
 * @param {{storeName?:string,title?:string,meta?:string[],brand?:boolean}} [options]
 * @returns {string}
 */
export const buildBrandedCsv = (rows, options = {}) => {
  const headers = resolveHeaders(rows);
  return serialize(headers, rows, resolveBrandLines(options));
};

/**
 * Downloads data as a branded CSV.
 * @param {string} filename
 * @param {Array<Record<string, unknown>>} rows
 * @param {{storeName?:string,title?:string,meta?:string[],brand?:boolean}} [options]
 * @returns {void}
 */
export const downloadCsv = (filename, rows, options = {}) => {
  const csv = buildBrandedCsv(rows, options);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  URL.revokeObjectURL(url);
};

const resolveHeaders = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const keys = new Set();
  rows.forEach((row) => Object.keys(row || {}).forEach((key) => keys.add(key)));
  return Array.from(keys);
};

const serialize = (headers, rows, brandLines = []) => {
  const safeHeaders = headers || [];
  const lines = [];
  // Branding preamble — centered across the table so it reads as a title band.
  (brandLines || []).forEach((line) => lines.push(centerLineToRow(line, safeHeaders.length)));
  lines.push(safeHeaders.map(escapeCell).join(','));
  (rows || []).forEach((row) => {
    const cells = safeHeaders.map((header) => escapeCell(row?.[header]));
    lines.push(cells.join(','));
  });
  return `${lines.join('\n')}\n`;
};

const escapeCell = (value) => {
  const raw = value === null || value === undefined ? '' : String(value);
  const escaped = raw.replace(/"/g, '""');
  return `"${escaped}"`;
};

const triggerDownload = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
};
